import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const CostOfDebt = () => {
  // State variables for Cost of Debt calculation
  const [totalInterestExpense, setTotalInterestExpense] = useState(0); // Total interest expense
  const [totalDebt, setTotalDebt] = useState(0); // Total debt
  const [costOfDebt, setCostOfDebt] = useState(0); // Calculated cost of debt

  // Event handlers to update state values
  const handleInterestExpenseChange = (e) => setTotalInterestExpense(parseFloat(e.target.value) || 0);
  const handleDebtChange = (e) => setTotalDebt(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the Cost of Debt
  useEffect(() => {
    // Cost of Debt = Total Interest Expense / Total Debt
    if (totalDebt > 0) {
      const cost = totalInterestExpense / totalDebt;
      setCostOfDebt(cost);
    }

     // This will initialize or reinitialize all popovers on the page whenever the component renders
     const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [totalInterestExpense, totalDebt]);

  return (
    <Layout title="Cost of Debt Calculator" path="cost-of-debt">
      <section className="section-padding" id="cost-of-debt">
        <div className="container mt-5">
          <div className="row">
            {/* Cost of Debt Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Cost of Debt Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Interest Expense */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalInterestExpense" className="form-label">
                        Total Interest Expense
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Interest Expense:</strong> The total amount of interest paid on outstanding debt during a specific period."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalInterestExpense"
                        placeholder="Enter total interest expense"
                        value={totalInterestExpense || ""}
                        onChange={handleInterestExpenseChange}
                      />
                    </div>

                    {/* Input for Total Debt */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalDebt" className="form-label">
                        Total Debt
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Total Debt:</strong> The total amount of debt held by the company. It includes loans, bonds, and other financial obligations."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalDebt"
                        placeholder="Enter total debt"
                        value={totalDebt || ""}
                        onChange={handleDebtChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Cost of Debt:{" "}
                        <span className="fw-bold ms-3">
                          {costOfDebt === 0
                            ? "0"
                            : (costOfDebt * 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{" "}
                          %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Cost of Debt
                </h3>

                {/* What is Cost of Debt? */}
                <h4 className="mt-3 mb-3">What is Cost of Debt?</h4>
                <p>
                  The **Cost of Debt** refers to the effective rate that a company pays on its borrowed funds. It is used to evaluate the cost-effectiveness of the company’s borrowing strategy, which is critical for financial planning and decision-making.
                </p>

                {/* Why Use the Cost of Debt? */}
                <h4 className="mt-4 mb-3">Why Use the Cost of Debt?</h4>
                <p>
                  Understanding the cost of debt helps companies assess how expensive their borrowing is and the impact it has on their overall financial health. By calculating this metric, businesses can optimize their debt strategy, lower financing costs, and make informed investment decisions.
                </p>

                {/* Benefits of Using the Cost of Debt */}
                <h4 className="mt-4 mb-3">Benefits of Using the Cost of Debt</h4>
                <ul>
                  <li><strong>Assists in Financial Planning and Debt Management:</strong> Helps companies plan for debt servicing and ensure they are not overleveraged.</li>
                  <li><strong>Helps Businesses Assess Their Cost of Financing:</strong> Enables businesses to evaluate whether they are obtaining financing at reasonable rates.</li>
                  <li><strong>Supports Budgeting for Loan Repayments:</strong> Helps companies forecast future financial obligations and plan budgets accordingly.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use the Cost of Debt?</h4>
                <p>
                  This calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Finance Teams:</strong> Helps corporate finance teams track and manage debt costs.</li>
                  <li><strong>Corporate Finance:</strong> Assists in overall financial strategy, decision-making, and capital budgeting.</li>
                  <li><strong>Investors:</strong> Provides valuable insight into the cost of capital and financial sustainability of a company.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CostOfDebt;
